import { type ChangeEvent } from 'react';
import { Box, Icon, Label, LabeledControl, Paragraph, Switch, Tag, Text, ToggleCard } from '@zillow/constellation-10';
import {
  IconCheckmarkCircleFilled,
  IconCheckmarkCircleOutline,
  IconLightningFilled,
  IconLightningOutline,
} from '@zillow/constellation-icons';
import { TourConfirmationType } from '@zg-rentals/ts-z-calendar-client';

const TourTypeCards = [
  {
    id: TourConfirmationType.INSTANT,
    title: 'Book instantly',
    subTitle: 'MOST EFFICIENT',
    description: 'Renters directly book a time from the availability provided on your listing.',
  },
  {
    id: TourConfirmationType.LANDLORD_CONFIRM,
    title: 'Review & confirm',
    subTitle: 'MORE CONTROL',
    description: 'Renters request a time from the availability on your listing, then you confirm or decline.',
  },
];

type BookingMethodBodyProps = {
  isTouringEnabled: boolean;
  isTouringEnabledFromApi: boolean;
  tourConfirmationType?: TourConfirmationType;
  onSelectBookingMethod: (tourConfirmationType: TourConfirmationType) => void;
  onToggleEnableTouring: (event: ChangeEvent<HTMLInputElement>) => void;
};

const BookingMethodBody = ({
  isTouringEnabled,
  isTouringEnabledFromApi,
  tourConfirmationType,
  onSelectBookingMethod,
  onToggleEnableTouring,
}: BookingMethodBodyProps) => {
  const bodyText = isTouringEnabledFromApi
    ? 'If you already chose a booking method but want to book tours on your own instead, turn off tour booking.'
    : 'You can change your settings anytime. Skip this if you prefer to book tours on your own.';
  const gap = isTouringEnabledFromApi ? 'default' : 'loose';

  const getTourIcon = (confirmationType: string, isSelected: boolean) => {
    if (confirmationType === TourConfirmationType.INSTANT) {
      return isSelected ? <IconLightningFilled /> : <IconLightningOutline />;
    }

    return isSelected ? <IconCheckmarkCircleFilled /> : <IconCheckmarkCircleOutline />;
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap }}>
      <Paragraph>{bodyText}</Paragraph>
      {isTouringEnabledFromApi && (
        <LabeledControl
          control={
            <Switch
              data-testid="BookingMethodBody-EnableTouringToggle"
              checked={isTouringEnabled}
              onChange={onToggleEnableTouring}
            />
          }
          label={<Label css={{ fontWeight: 'bold' }}>Tour scheduling</Label>}
          labelPosition="left"
        />
      )}

      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: { base: 'tight', md: 'default' },
        }}
      >
        {TourTypeCards.map((card, index) => (
          <ToggleCard
            data-testid={`BookingMethodBody-ToggleCard-${index}`}
            elevated={false}
            key={card.id}
            selected={card.id === tourConfirmationType}
            onClick={() => onSelectBookingMethod(card.id)}
            disabled={isTouringEnabledFromApi && !isTouringEnabled}
          >
            <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
              <Box
                css={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
                  <Icon
                    size="md"
                    css={{
                      color: card.id === tourConfirmationType ? 'blue.500' : 'black',
                    }}
                  >
                    {getTourIcon(card.id, card.id === tourConfirmationType)}
                  </Icon>
                  <Text textStyle="body-sm-bold">{card.title}</Text>
                </Box>
                <Tag>{card.subTitle}</Tag>
              </Box>
              <Text textStyle="body-sm">{card.description}</Text>
            </Box>
          </ToggleCard>
        ))}
      </Box>
    </Box>
  );
};

export default BookingMethodBody;
